import { useState } from "react";
import { Plus, RefreshCw, Trash2 } from "lucide-react";
import { PLATFORM_META } from "@/constants/platforms";
import { Platform } from "@/types";
import { useAccountStore } from "@/store/account-store";
import { PlatformSelect } from "@/components/forms/PlatformSelect";
import { Skeleton } from "@/components/ui/Skeleton";

export function SocialAccountManager() {
  const { accounts, addAccount, removeAccount, initialized } = useAccountStore();
  const [handle, setHandle] = useState("");
  const [platform, setPlatform] = useState<Platform>("instagram");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);

  const handleAdd = async () => {
    setError(null);
    setLoading(true);
    if (!handle.trim()) {
      setError("Handle required");
      setLoading(false);
      return;
    }
    try {
      await addAccount({ platform, handle: handle.trim() });
      setHandle("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to add account");
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id: string) => {
    setRemoving(id);
    try {
      await removeAccount(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to remove account");
    } finally {
      setRemoving(null);
    }
  };

  return (
    <section className="rounded-3xl border border-white/10 bg-zinc-900/70 p-5 text-white">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-sm uppercase tracking-wide text-lime-300">Linked channels</p>
          <h2 className="text-xl font-semibold">Social accounts</h2>
        </div>
      </header>

      <div className="mb-4 flex gap-2">
        <PlatformSelect
          value={platform}
          onChange={(value) => setPlatform(value)}
        />
        <input
          value={handle}
          onChange={(event) => setHandle(event.target.value)}
          placeholder="@ghostloops"
          className="w-full rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-sm"
        />
        <button
          onClick={handleAdd}
          disabled={loading}
          className="rounded-2xl bg-lime-400 px-4 text-black disabled:opacity-50"
        >
          {loading ? "..." : <Plus className="h-4 w-4" />}
        </button>
      </div>
      {error && <p className="text-sm text-red-200">{error}</p>}

      <div className="mt-4 space-y-3">
        {!initialized && (
          <>
            <div className="rounded-2xl border border-white/10 bg-black/40 p-3 space-y-2">
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-black/40 p-3 space-y-2">
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-3 w-32" />
                </div>
              </div>
            </div>
          </>
        )}
        {initialized && accounts.map((account) => (
          <div
            key={account.id}
            className="flex items-center gap-3 rounded-2xl border border-white/10 bg-black/40 p-3"
          >
            <img
              src={PLATFORM_META[account.platform].logo}
              alt={account.platform}
              width={32}
              height={32}
              className="rounded-full border border-white/10 bg-white/5 p-1"
            />
            <div className="grow">
              <p className="font-medium">{account.handle}</p>
              <p className="text-xs text-white/60">
                Slots {account.slotsUsed}/{account.slotsLimit} · Status {account.status}
              </p>
            </div>
            <button className="rounded-2xl bg-white/10 px-3 py-2 text-xs">
              <RefreshCw className="mr-1 inline h-3 w-3" /> Fetch metadata
            </button>
            <button
              onClick={() => handleRemove(account.id)}
              disabled={removing === account.id}
              className="rounded-2xl bg-red-500/10 p-2 text-red-200 disabled:opacity-50"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
